<template>
  <div>
    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="tokens"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
    >
      <template #cell(action)="{ data }">
        <div
          v-if="!data.revoked"
          v-tooltip.top="t('supplierAuthTokens.revoke')"
        >
          <FontAwesomeIcon
            class="revoke-icon"
            :icon="faBan"
            @click="revokeToken(data._id)"
          />
        </div>
        <FontAwesomeIcon v-else class="revoke-icon disabled" :icon="faBan" />
      </template>
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { useToast } from '@/composables/useToast';
  import { endpointsList, formatDateForDisplay } from '@/helpers';
  import axios from 'axios';
  import { ISupplierAuthTokenDto } from 'types';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { faBan } from '@fortawesome/free-solid-svg-icons';

  const props = defineProps<{
    fetchTokens: () => Promise<void>;
    supplierId: string;
    tokens: ISupplierAuthTokenDto[];
  }>();

  const { t } = useI18n();
  const { createErrorToast, createSuccessToast } = useToast();
  const { pageOptions, filters, isLoading } =
    useTableWrapper<ISupplierAuthTokenDto>(
      TableMode.CLIENT_SIDE,
      'createdAt',
      props.fetchTokens,
    );

  async function revokeToken(tokenId: string): Promise<void> {
    isLoading.value = true;
    try {
      await axios.delete(endpointsList.supplierAuthTokens.revokeToken(tokenId));
      createSuccessToast(t('supplierAuthTokens.tokenRevoked'));
      await props.fetchTokens();
    } catch {
      createErrorToast(t('supplierAuthTokens.errors.errorRevokingToken'));
    } finally {
      isLoading.value = false;
    }
  }

  const tableFields = computed(() =>
    new TableFieldsBuilder<ISupplierAuthTokenDto>({
      sortable: true,
      labelTranslationKey: 'supplierAuthTokens',
    })
      .addField('name', {
        width: '40%',
      })
      .addField('createdAt', {
        width: '15%',
        valueGetter: (data) => formatDateForDisplay(data.createdAt),
      })
      .addField('expiresAt', {
        width: '15%',
        valueGetter: (data) => formatDateForDisplay(data.expiresAt),
      })
      .addField('revoked', {
        width: '12.5%',
        valueTranslationKey: 'generic.booleanYesNo',
      })
      .addField('expired', {
        width: '12.5%',
        valueTranslationKey: 'generic.booleanYesNo',
      })
      .addField('action' as any, {
        sortable: false,
        width: '5%',
        label: '',
      })
      .build(),
  );

  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<ISupplierAuthTokenDto>({
      translationKey: 'supplierAuthTokens',
    })
      .addInputFilter('name')
      .build(),
  );
</script>

<style scoped lang="scss">
  .revoke-icon {
    cursor: pointer;
    color: $danger-color;
    &.disabled {
      cursor: default;
      opacity: 0.5;
    }
  }
</style>
